import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { readAppConfig, type AuthenticationContextType } from "@atomic/app-shell";
import { APP_ENV } from "../../app_env";

/**
 * The Apollo client over the backend gateway — LMNT's own pages and hooks
 * query through it (the generated documents in services/__generated__), and
 * several modules hold the instance directly (runSelectionContext, the cube
 * hooks), so it stays a module singleton.
 *
 * The session is the shell's (@atomic/app-shell AuthenticationProvider): every
 * request goes through its `fetchWithToken`, which sends the same headers the
 * retired AuthenticationService link did — Authorization (the access token,
 * for the API Gateway JWT authorizer), X-IdToken (the ID token the gateway
 * identifies the user by; it accepts this spelling beside x-atomic-idtoken),
 * x-atomic-customerid (the baked company) and X-Impersonation while a View As
 * session is on. Token refresh is the provider's; the fetch is re-bound on
 * every render of the bridge (providers.tsx), so a request made after a
 * refresh carries the new tokens.
 */
type AuthenticatedFetch = AuthenticationContextType["fetchWithToken"];

let authenticatedFetch: AuthenticatedFetch | null = null;

/** Bind the shell's session transport. Called by the Apollo bridge on every
 *  render, inside the shell's auth gate — so by the time a page queries, the
 *  fetch is the signed-in one. */
export function bindAuthenticatedFetch(fn: AuthenticatedFetch | null): void {
  authenticatedFetch = fn;
}

// The gateway's GraphQL URL from the shell's contract: VITE_HTTP_API_V2_HOST in
// a deployment, VITE_BACKENDGATEWAY_URL (aliased in app_env.ts) in local dev.
// Empty when unconfigured — createClientApp then renders its configuration
// error instead of the app, so nothing queries this.
const { gatewayGraphqlUrl } = readAppConfig(APP_ENV);

const httpLink = createHttpLink({
  uri: gatewayGraphqlUrl ?? "",
  fetch: (input, init) => {
    if (!authenticatedFetch) {
      return Promise.reject(new Error("The backend gateway session is not ready."));
    }
    return authenticatedFetch(input as string | URL | Request, init);
  },
});

export const apolloClient = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
  },
});

// Helper function to get Apollo Client instance
export const getApolloClient = () => apolloClient;
